import { useMemo, useRef, type FC, type ReactNode } from 'react';
import { useSelector } from 'react-redux';

import { BreakpointProvider } from '@/core/tamagoshi/MediaQuery/MediaQuery';
import { pageTypeSelector } from '@/domains/core/page/page.selectors';
import { useEligibilityCoupon } from '@/domains/loyalty/modules/CouponEligibility/hooks/useEligibilityCoupon';

import { useBundleCoupons } from '../../components/Coupon/hooks/useBundleCoupons';
import { useProductBundle } from '../ProductBundle/ProductBundle.context';
import CouponContext from './Coupon.context';
import type { ICouponState } from './coupon.interfaces';

const CouponProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const placeholderTerms = useRef<HTMLDivElement>(null);
  // Info needed from bundle and main products
  const { productList } = useProductBundle();

  // Check if the main product has coupons
  const mainProductCoupon = useEligibilityCoupon();
  const pageType = useSelector(pageTypeSelector);

  // Here make call to new endpoint to check if coupon exist
  const { bundleCoupon } = useBundleCoupons(
    productList,
    pageType !== 'model_listing',
    !!mainProductCoupon,
  );

  const showUnavailableCoupon = useMemo(
    () => productList.some((product) => !product.isInBundle),
    [productList],
  );

  const contextValue: ICouponState = useMemo(
    () => ({
      applicableCoupon: bundleCoupon,
      isCouponEnabled: pageType !== 'model_listing',
      showUnavailableCoupon,
      placeholderTerms,
      shouldRenderCoupon: true,
    }),
    [bundleCoupon, pageType, showUnavailableCoupon],
  );

  return (
    <CouponContext.Provider value={contextValue}>
      <BreakpointProvider>{children}</BreakpointProvider>
    </CouponContext.Provider>
  );
};

CouponProvider.displayName = 'CouponProvider';

export default CouponProvider;
